import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import {
  Email,
  Security,
  LocationOn,
  People,
  EmojiEvents,
  Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Handle potential errors gracefully
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Landing page error:', event.error);
      setError(event.error?.message || 'An unexpected error occurred');
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Show error state if there's an error
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom color="error">
              Something went wrong
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setError(null)}
              sx={{ mr: 2 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const features = [
    { icon: LocationOn, title: 'Trouver des Courts', desc: 'Découvrez instantanément les meilleurs courts près de chez vous.' },
    { icon: People, title: 'Matchmaking', desc: 'Connectez-vous avec des joueurs de votre niveau.' },
    { icon: EmojiEvents, title: 'Classements', desc: 'Grimpez dans les classements locaux et competez.' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: '#0a0a0a',
        overflow: 'hidden',
        position: 'relative',
        color: 'white',
      }}
    >
      {/* Abstract Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            width: '50%',
            height: '50%',
            bgcolor: 'primary.main',
            opacity: 0.2,
            borderRadius: '50%',
            filter: 'blur(120px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            right: '-10%',
            width: '40%',
            height: '40%',
            bgcolor: '#2563eb',
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(100px)',
          }}
        />
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            src="/PadPal.png"
            alt="PadPal Logo"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              letterSpacing: '-0.02em',
            }}
          >
            PadPal
          </Typography>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          zIndex: 10,
          pb: { xs: 6, sm: 8, md: 10 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ position: 'relative', marginBottom: 32 }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'primary.main',
              opacity: 0.4,
              filter: 'blur(40px)',
              borderRadius: '50%',
              transition: 'opacity 0.7s',
              '&:hover': { opacity: 0.75 },
            }}
          />
          <Box
            component="img"
            src="/PadPal.png"
            alt="PadPal App Icon"
            sx={{
              position: 'relative',
              width: { xs: 120, sm: 160, md: 200, lg: 224 },
              height: { xs: 120, sm: 160, md: 200, lg: 224 },
              borderRadius: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.5s ease-out',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: { xs: 2, sm: 3 },
              color: 'white',
              lineHeight: 1.1,
            }}
          >
            PadPal
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: { xs: '100%', sm: '32rem', md: '36rem' },
              mb: { xs: 4, sm: 5 },
              lineHeight: 1.75,
              mx: 'auto',
              px: { xs: 1, sm: 0 },
            }}
          >
            Le compagnon ultime pour le joueur de padel moderne. Trouvez des courts, rejoignez des matchs et suivez vos progrès avec style.
          </Typography>
        </motion.div>

        {/* App Download Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: 700,
            width: '100%',
          }}
        >
          {/* iOS App Store */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            flex: { xs: '1 1 100%', sm: 1 },
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}>
            <Link
              href="https://apps.apple.com/fr/app/padpal/id6749649779"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: 'center' }}
            >
              <Box
                component="img"
                src="/appstore.svg"
                alt="Télécharger sur l'App Store"
                sx={{
                  height: { xs: 45, sm: 50, md: 60 },
                  width: 'auto',
                  maxWidth: { xs: '180px', sm: 'none' },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    filter: 'brightness(0.9)',
                  },
                }}
              />
            </Link>
          </Box>
          
          {/* Android - Play Store */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            flex: { xs: '1 1 100%', sm: 1 },
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}>
            <Link
              href="https://play.google.com/store/apps/details?id=com.saffist3r.padpal"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: 'center' }}
            >
              <Box
                component="img"
                src="/playstore.svg"
                alt="Télécharger sur Google Play"
                sx={{
                  height: { xs: 45, sm: 50, md: 60 },
                  width: 'auto',
                  maxWidth: { xs: '180px', sm: 'none' },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    filter: 'brightness(0.9)',
                  },
                }}
              />
            </Link>
          </Box>
        </motion.div>

        {/* Feature Grid */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: { xs: 3, sm: 3, md: 3 },
            mt: { xs: 6, sm: 8, md: 12 },
            width: '100%',
            maxWidth: { xs: '100%', sm: '48rem', md: '64rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          {features.map((feature, i) => (
              <Paper
              key={i}
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: { xs: '1rem', sm: '1.5rem' },
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                textAlign: 'left',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.06)',
                },
                backdropFilter: 'blur(8px)',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '1rem',
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: 'primary.main',
                }}
              >
                <feature.icon sx={{ fontSize: 24 }} />
              </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: 'white',
              fontSize: { xs: '1rem', sm: '1.125rem' },
            }}
          >
            {feature.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              lineHeight: 1.6,
            }}
          >
            {feature.desc}
          </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          py: { xs: 4, sm: 6 },
          mt: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 4,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src="/PadPal.png"
                  alt="PadPal Logo"
                  style={{
                    height: 32,
                    width: 'auto',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    letterSpacing: '-0.02em',
                    color: 'white',
                  }}
                >
                  PadPal
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                © {new Date().getFullYear()} PadPal. Tous droits réservés.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', sm: 'flex-end' } }}>
              <Stack spacing={1.5} sx={{ alignItems: { xs: 'center', sm: 'flex-end' } }}>
                <Link
                  href="mailto:contact@padpal.fr"
                  underline="hover"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s',
                  }}
                >
                  <Email sx={{ fontSize: 16 }} />
                  contact@padpal.fr
                </Link>
                <Link
                  component="button"
                  onClick={() => navigate('/privacy')}
                  underline="hover"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s',
                  }}
                >
                  <Security sx={{ fontSize: 16 }} />
                  Politique de confidentialité
                </Link>
                <Link
                  component="button"
                  onClick={() => navigate('/changelog')}
                  underline="hover"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s',
                  }}
                >
                  <Star sx={{ fontSize: 16 }} />
                  Journal des modifications
                </Link>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};
